import type TauriDatabase from "@tauri-apps/plugin-sql";
import { type Database as BunDatabase, type SQLQueryBindings } from "bun:sqlite";
import shortUUID from "short-uuid";

import { sql } from "../SqlString";
import { type SqliteTableDef } from "../schema";
import { createTableSql } from "./createTableSql";
import { migrateDataSql } from "./migrateDataSql";
import { runGetTableXInfo } from "./runGetTableXInfo";

/**
 * Migrates the `currentTable` to the new table specification indicated by `desiredTable`. Most
 * commonly this is done when we need to add, delete, or modify columns in the table.
 */
export async function runMigration(
	db: BunDatabase | TauriDatabase,
	desiredTable: SqliteTableDef,
	options?: { oldColumnName?: string; newColumnName?: string }
): Promise<void> {
	const run = (statement: string, params: SQLQueryBindings[] = []) =>
		"prepare" in db ? db.prepare(statement, params).run() : db.execute(statement, params);

	// Start a transaction
	await run("BEGIN TRANSACTION");

	// Get current table info
	const tableName = desiredTable.tableName;
	const currentTable = await runGetTableXInfo(db, tableName);

	try {
		// If table doesn't exist, just create it
		const createIfNotExists = createTableSql(desiredTable, true);
		await run(createIfNotExists.statement, createIfNotExists.params);

		const suffix = shortUUID.generate();
		const tempTableName = `${tableName}_migration_${suffix}`;

		// Create new table with desired schema
		const desiredTempTable: SqliteTableDef = { ...desiredTable, tableName: tempTableName };
		const { statement, params } = createTableSql(desiredTempTable);
		await run(statement, params);

		// Copy data from old table to new table
		const copyDataSql = migrateDataSql(currentTable, desiredTempTable, options);
		await run(copyDataSql.statement, copyDataSql.params);

		// Drop old table
		const dropTableSql = sql`DROP TABLE ${sql.quote(tableName)}`;
		await run(dropTableSql.statement, dropTableSql.params);

		// Rename new table to original name
		const renameTableSql = sql`ALTER TABLE ${sql.quote(tempTableName)} RENAME TO ${sql.quote(tableName)}`;
		await run(renameTableSql.statement, renameTableSql.params);

		await run("COMMIT");
	} catch (error) {
		console.error("Error running migration plan", error);
		await run("ROLLBACK");
		throw error;
	}
}
